import { useState, useRef, useMemo, useLayoutEffect } from 'react';
import { apiPatch, apiGet, apiDelete } from '../../api/client';
import { type ConfigSaveResponse, saveResultMessage, needsAttentionText } from '../../lib/applyResult';
import { type TransportUsage, removeConnectionMessage, REMOVE_CONNECTION_BUTTON } from '../../lib/removeConnection';
import { ConfirmModal } from '../AppModal';
import { configSectionLabel } from '../../lib/definitions/settings';
import { isFieldValueSet, fieldDomId } from './schema';
import type { SchemaField } from './schema';
import { PaneDescription, FieldEditor } from './FieldEditor';
import { recordTitle, blankItemFromFields, recordNounSingular } from './records';


// One instance row of a dynamic-keyed section's master-detail state — the
// same baseline/current split ListSectionEditor's RecordRow uses, except
// `id` (the instance's own dict key, e.g. "meshtastic_vnode") IS its
// identity, not an internal React key standing in for a list position. An
// existing instance's `id` is immutable once loaded (renaming would be a
// remove+add, which this editor doesn't support — see the section's own
// schema description for why removal isn't possible through this API).
interface DynamicInstanceRow {
  id: string;
  isNew: boolean;
  baseline: Record<string, unknown>;
  current: Record<string, unknown>;
}


// Master-detail editor for a single dynamic-keyed section (today: only
// `transports`), mirroring ListSectionEditor's layout/patterns (same CSS
// classes, same primary/secondary field split, same "Optional details"
// disclosure) but keyed by an operator-chosen instance id rather than list
// position, and saved with per-instance, per-field PATCHes rather than a
// full-list replace — see this file's header comment on the dynamic-keyed
// save contract.
//
// Instance removal: now a real, working action (2026-09-27) —
// DELETE /api/config/{section}/{key} (config_schema.py's DYNAMIC_KEYED_
// SECTIONS docstring: PATCH can only add/change a key, this is the
// counterpart that can actually delete one). "Remove" always confirms
// first (window.confirm naming the instance, same convention as
// BehaviourTab.tsx's saved-view delete) since there is no undo from
// here — the entry must be re-added by hand afterward. The response's
// own `applied` list is shown on the result line like a normal save. When the section's own item
// shape also declares a boolean `enabled` field, the detail pane still
// offers the softer "Disable" action alongside Remove — sets it to false
// without deleting the entry, for a temporary stop rather than a
// permanent removal.
export function DynamicKeyedSectionEditor({
  path,
  description,
  itemFields,
  initialValue,
  highlightPath,
}: {
  path: string;
  description?: string;
  itemFields: SchemaField[];
  initialValue: Record<string, Record<string, unknown>>;
  highlightPath: string | null;
}) {
  const [rows, setRows] = useState<DynamicInstanceRow[]>(() =>
    Object.entries(initialValue).map(([id, item]) => ({ id, isNew: false, baseline: item, current: { ...item } })),
  );
  const [selectedId, setSelectedId] = useState<string | null>(rows[0]?.id ?? null);
  const [filterText, setFilterText] = useState("");
  const [addingId, setAddingId] = useState(false);
  const [newId, setNewId] = useState("");
  const detailsRef = useRef<HTMLDetailsElement | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  const isDirty = rows.some((r) => r.isNew || JSON.stringify(r.current) !== JSON.stringify(r.baseline));

  // row.id is always the operator-typed instance id here (confirmAddInstance
  // requires a non-empty one) — a real, meaningful fallback, unlike
  // ListSectionEditor's synthetic row key.
  const decorated = rows.map((row) => ({ row, title: recordTitle(row.current, row.id) }));
  const filtered = decorated.filter(({ row, title }) => {
    if (!filterText.trim()) return true;
    const q = filterText.trim().toLowerCase();
    return title.toLowerCase().includes(q) || row.id.toLowerCase().includes(q);
  });

  const selected = rows.find((r) => r.id === selectedId) ?? null;

  // Same "split frozen against baseline" rule as ListSectionEditor — see
  // that component's own comment for why.
  const primaryKeys = useMemo(() => {
    if (!selected) return new Set<string>();
    const s = new Set<string>();
    for (const f of itemFields) {
      if (Object.prototype.hasOwnProperty.call(selected.baseline, f.path) && isFieldValueSet(selected.baseline[f.path])) {
        s.add(f.path);
      }
    }
    return s;
  }, [selected, itemFields]);

  // The display name (`label`) is always a primary field, shown first as
  // "Name" with the id under it; it is not repeated in the field grids.
  const nameField = itemFields.find((f) => f.path === "label" && !f.choices?.length) ?? null;
  const primaryFields = itemFields.filter((f) => primaryKeys.has(f.path) && f !== nameField);
  const secondaryFields = itemFields.filter((f) => !primaryKeys.has(f.path) && f !== nameField);
  const filledSecondaryCount = selected ? secondaryFields.filter((f) => isFieldValueSet(selected.current[f.path])).length : 0;

  const enabledField = itemFields.find((f) => f.path === "enabled" && f.type === "bool") ?? null;

  // Same uncontrolled-<details> reasoning as ListSectionEditor — see that
  // component's own comment for why this can't be a controlled `open` prop.
  useLayoutEffect(() => {
    if (!detailsRef.current || !selected) return;
    const forceOpenForMatch = !!highlightPath && secondaryFields.some((f) => f.path === highlightPath);
    detailsRef.current.open = selected.isNew || forceOpenForMatch;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, highlightPath]);

  function updateField(rowId: string, fieldPath: string, value: unknown) {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, current: { ...r.current, [fieldPath]: value } } : r)));
    setSaveMsg(null);
  }

  function confirmAddInstance() {
    const id = newId.trim();
    if (!id) {
      setSaveMsg({ kind: "error", text: "An instance id is required." });
      return;
    }
    if (rows.some((r) => r.id === id)) {
      setSaveMsg({ kind: "error", text: `An instance named "${id}" already exists.` });
      return;
    }
    const blank = blankItemFromFields(itemFields);
    setRows((prev) => [...prev, { id, isNew: true, baseline: blank, current: { ...blank } }]);
    setSelectedId(id);
    setNewId("");
    setAddingId(false);
    setSaveMsg(null);
  }

  async function onSave() {
    if (!isDirty) return;
    setSaving(true);
    setSaveMsg(null);
    // Per-instance, per-field patch — see this file's header comment on
    // the dynamic-keyed save contract. A new instance sends its whole
    // current object (there is no prior on-disk state to diff against); an
    // existing instance sends only the fields that actually changed.
    const patch: Record<string, Record<string, unknown>> = {};
    for (const row of rows) {
      if (row.isNew) {
        patch[row.id] = row.current;
        continue;
      }
      const changed: Record<string, unknown> = {};
      for (const f of itemFields) {
        if (JSON.stringify(row.baseline[f.path]) !== JSON.stringify(row.current[f.path])) {
          changed[f.path] = row.current[f.path];
        }
      }
      if (Object.keys(changed).length > 0) patch[row.id] = changed;
    }
    try {
      const resp = await apiPatch<ConfigSaveResponse>(`/api/config/${encodeURIComponent(path)}`, { patch });
      setSaveMsg(saveResultMessage(resp?.applied));
      setRows((prev) => prev.map((r) => ({ ...r, baseline: r.current, isNew: false })));
    } catch (e) {
      setSaveMsg({ kind: "error", text: e instanceof Error ? e.message : "Save failed." });
    } finally {
      setSaving(false);
    }
  }

  // A saved connection (transports) is removed together with the data it stored: the confirm
  // states the counts first and its one button does both, so there is no second confirm.
  const [removeDialog, setRemoveDialog] = useState<{ rowId: string; label: string; message: string } | null>(null);

  async function onRemove(rowId: string) {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    const title = recordTitle(row.current, rowId);
    const label = title && title !== rowId ? `"${title}" (${rowId})` : `"${rowId}"`;
    if (path === "transports" && !row.isNew) {
      setSaveMsg(null);
      try {
        const usage = await apiGet<TransportUsage>(`/api/config/transports/${encodeURIComponent(rowId)}/usage`);
        setRemoveDialog({ rowId, label, message: removeConnectionMessage(title || rowId, usage) });
      } catch (e) {
        setSaveMsg({ kind: "error", text: e instanceof Error ? e.message : "Could not read what this connection stored." });
      }
      return;
    }
    if (!window.confirm(`Remove ${label}? This cannot be undone from here — it would need to be re-added by hand.`)) return;
    await doRemove(rowId, label);
  }

  async function doRemove(rowId: string, label: string) {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    if (row.isNew) {
      // Never saved — just drop it locally, no API call needed.
      setRows((prev) => prev.filter((r) => r.id !== rowId));
      setSelectedId((prev) => (prev === rowId ? rows.find((r) => r.id !== rowId)?.id ?? null : prev));
      return;
    }

    setSaving(true);
    setSaveMsg(null);
    try {
      const resp = await apiDelete<ConfigSaveResponse>(
        `/api/config/${encodeURIComponent(path)}/${encodeURIComponent(rowId)}`,
      );
      setRows((prev) => prev.filter((r) => r.id !== rowId));
      setSelectedId((prev) => (prev === rowId ? rows.find((r) => r.id !== rowId)?.id ?? null : prev));
      const attention = needsAttentionText(resp?.applied);
      setSaveMsg({ kind: attention ? "error" : "ok", text: attention ? `Removed ${label}. ${attention}` : `Removed ${label}.` });
    } catch (e) {
      setSaveMsg({ kind: "error", text: e instanceof Error ? e.message : "Remove failed." });
    } finally {
      setSaving(false);
    }
  }

  const noun = recordNounSingular(path);

  return (
    <div className="settings-pane">
      {removeDialog && (
        <ConfirmModal
          title="Remove Connection"
          message={removeDialog.message}
          onCancel={() => setRemoveDialog(null)}
          actions={[
            {
              label: REMOVE_CONNECTION_BUTTON,
              kind: "danger",
              disabled: saving,
              onClick: () => {
                const d = removeDialog;
                setRemoveDialog(null);
                void doRemove(d.rowId, d.label);
              },
            },
          ]}
        />
      )}
      {description && <PaneDescription text={description} label={configSectionLabel(path)} />}
      <div className="settings-record-toolbar">
        <input
          type="text"
          className="settings-record-filter"
          placeholder="Filter connections…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          aria-label="Filter connections"
        />
        {addingId ? (
          <>
            <input
              type="text"
              className="settings-record-filter"
              placeholder="New connection id"
              value={newId}
              onChange={(e) => setNewId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") confirmAddInstance();
                else if (e.key === "Escape") {
                  setAddingId(false);
                  setNewId("");
                }
              }}
              aria-label="New connection id"
              autoFocus
            />
            <button type="button" className="settings-record-add-btn" onClick={confirmAddInstance}>
              Add
            </button>
            <button
              type="button"
              className="settings-save-btn"
              onClick={() => {
                setAddingId(false);
                setNewId("");
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="settings-record-add-btn" onClick={() => setAddingId(true)}>
            + Add {noun}
          </button>
        )}
        {saveMsg && (
          <span className="settings-save-status" data-kind={saveMsg.kind}>
            {saveMsg.text}
          </span>
        )}
        <button className="settings-save-btn" onClick={onSave} disabled={saving || !isDirty}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="settings-record-split">
        <div className="settings-record-list">
          {filtered.length === 0 && <div className="settings-record-empty">No connections match.</div>}
          {filtered.map(({ row, title }) => (
            <button
              key={row.id}
              type="button"
              className="settings-record-row"
              data-active={row.id === selectedId ? "true" : "false"}
              onClick={() => setSelectedId(row.id)}
            >
              <span className="settings-record-row-title">
                {(row.isNew || JSON.stringify(row.current) !== JSON.stringify(row.baseline)) && (
                  <span className="settings-record-dot" title="Unsaved changes" />
                )}
                {title}
              </span>
              <span className="settings-record-row-subtitle">{row.id}</span>
            </button>
          ))}
        </div>

        <div className="settings-record-detail">
          {!selected && <div className="settings-record-empty">No instance selected — add one to get started.</div>}
          {selected && (
            <>
              <div className="settings-record-detail-header">
                <h3>{recordTitle(selected.current, selected.id)}</h3>
                {enabledField && selected.current.enabled !== false && (
                  <button
                    type="button"
                    className="settings-record-remove-btn"
                    onClick={() => updateField(selected.id, "enabled", false)}
                    title="Turns this off and saves normally — this does not remove the instance from the configuration."
                  >
                    Disable
                  </button>
                )}
                <button
                  type="button"
                  className="settings-record-remove-btn"
                  onClick={() => onRemove(selected.id)}
                  disabled={saving}
                  title={path === "transports" ? "Deletes this connection and the data it stored — confirms first, with the counts, and cannot be undone from here." : "Deletes this instance from the configuration — confirms first, and cannot be undone from here."}
                >
                  Remove
                </button>
              </div>

              <div className="settings-fields-grid">
                {nameField && (
                  <div id={fieldDomId(path, nameField.path)}>
                    <label className={`settings-field${highlightPath === nameField.path ? " settings-field-search-hit" : ""}`}>
                      <span>Name</span>
                      <input
                        type="text"
                        value={typeof selected.current.label === "string" ? selected.current.label : ""}
                        onChange={(e) => updateField(selected.id, nameField.path, e.target.value)}
                      />
                    </label>
                    <label className="settings-field">
                      <span>ID (cannot be changed)</span>
                      <input type="text" value={selected.id} readOnly disabled />
                    </label>
                  </div>
                )}
                {primaryFields.map((f) => (
                  <div key={f.path} id={fieldDomId(path, f.path)}>
                    <FieldEditor
                      field={f}
                      value={selected.current[f.path] ?? f.default}
                      dirty={JSON.stringify(selected.baseline[f.path]) !== JSON.stringify(selected.current[f.path])}
                      highlighted={highlightPath === f.path}
                      onChange={(v) => updateField(selected.id, f.path, v)}
                    />
                  </div>
                ))}
              </div>

              {secondaryFields.length > 0 && (
                <details key={selected.id} ref={detailsRef} className="settings-record-optional">
                  <summary>
                    Optional details ({filledSecondaryCount} of {secondaryFields.length} set)
                  </summary>
                  <div className="settings-fields-grid">
                    {secondaryFields.map((f) => (
                      <div key={f.path} id={fieldDomId(path, f.path)}>
                        <FieldEditor
                          field={f}
                          value={selected.current[f.path] ?? f.default}
                          dirty={JSON.stringify(selected.baseline[f.path]) !== JSON.stringify(selected.current[f.path])}
                          highlighted={highlightPath === f.path}
                          onChange={(v) => updateField(selected.id, f.path, v)}
                        />
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
